import cn from '@integrations/classnames'
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from '@integrations/react'
import styles from './RowDetails.module.scss'

interface Props {
  open: boolean
  children: ReactNode
  className?: string
}

/** True when a click came from inside a row's details, so the row itself does not treat it as a (de)selection. */
export function isInsideRowDetails(event: MouseEvent): boolean {
  return event.target instanceof Element && event.target.closest('[data-row-details]') !== null
}

/** Details that slide open inside a list card (a `<td>`: it is a child of the table's `<tr>`). The content stays mounted until the closing slide has finished. */
function RowDetails({ open, children, className }: Props) {
  const ref = useRef<HTMLTableCellElement>(null)
  const [mounted, setMounted] = useState(open)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    setExpanded(false)
    // safety net for environments that never fire `transitionend` (hidden tab, no motion)
    const timer = setTimeout(() => setMounted(false), 400)
    return () => clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (mounted && open) {
      // Reading the layout makes the browser compute the collapsed state first; without it the collapsed and
      // expanded styles can land in the same frame and the slide never plays.
      ref.current?.getBoundingClientRect()
      setExpanded(true)
    }
  }, [mounted, open])

  function handleTransitionEnd(event: React.TransitionEvent<HTMLTableCellElement>) {
    if (event.target !== event.currentTarget || event.propertyName !== 'grid-template-rows') return
    if (open) {
      ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    } else {
      setMounted(false)
    }
  }

  return mounted ? (
    <td
      ref={ref}
      className={cn(styles['row-details'], 'row-details', className)}
      data-open={expanded}
      data-row-details
      onTransitionEnd={handleTransitionEnd}
    >
      <div className={cn(styles['row-details__inner'], 'row-details__inner')}>{children}</div>
    </td>
  ) : null
}

export default RowDetails
